// ============================================================
//  src/components/Navbar.jsx
// ============================================================

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LogoutModal from "./LogoutModal";
import config from "../config";
import products from "../data/products";

function StoreLogo() {
  const [imgFailed, setImgFailed] = useState(false);
  if (!imgFailed) {
    return (
      <img
        src={`${process.env.PUBLIC_URL || ''}/images/logo.png`}
        alt={config.storeName}
        onLoad={() => setImgFailed(false)}
        onError={() => setImgFailed(true)}
        className="nav-logo-img"
      />
    );
  }
  return (
    <span className="nav-logo-fallback">
      <i className="fa-solid fa-seedling nav-logo-icon" />
      <span className="nav-logo-accent">RK</span> Farm Foods
    </span>
  );
}

export default function Navbar({ cart, auth, setGlobalToast, setSelectedProduct }) {
  const { user, logout } = auth;
  const navigate = useNavigate();
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const [showLogout, setShowLogout] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSub, setOpenSub] = useState(null);

  async function handleLogout() {
    setShowLogout(false);
    if (setGlobalToast) setGlobalToast("You have been signed out successfully!");
    navigate("/");
    await logout();
  }

  const userName  = user?.name || "";
  const initial   = userName.charAt(0)?.toUpperCase() || "U";
  const firstName = userName.split(" ")[0] || "User";

  const goTo = (path) => {
    navigate(path);
    setMenuOpen(false);
    setOpenSub(null);
    window.scrollTo(0, 0);
  };

  const navigateProduct = (product) => {
    setSelectedProduct?.(product);
    navigate(`/product/${product.id}`);
    setMenuOpen(false);
    setOpenSub(null);
    window.scrollTo(0, 0);
  };

  const categories = [
    { label: 'Millets',           key: 'Millets' },
    { label: 'Traditional Rice',  key: 'Traditional Rice' },
    { label: 'Dry Fruits & Nuts', key: 'Dry Fruits & Nuts' },
    { label: 'Sweeteners',        key: 'Sweeteners' },
  ];

  const categorySlug = (key) =>
    key.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  return (
    <>
      {/* Promo bar */}
      <div className="promo-bar">
        <div className="promo-left">
          <a href={config.social?.facebook || '#'} target="_blank" rel="noreferrer" className="social-btn" aria-label="Facebook">
            <i className="fa-brands fa-facebook-f" />
          </a>
          <a href={config.social?.instagram || '#'} target="_blank" rel="noreferrer" className="social-btn" aria-label="Instagram">
            <i className="fa-brands fa-instagram" />
          </a>
        </div>
        <div className="promo-center">Free Delivery on orders above ₹999</div>
        <div className="promo-right">
          <a href={`tel:${config.phone.replace(/\s+/g, '')}`} className="promo-phone">
            <i className="fa-solid fa-phone promo-phone-icon" />
            {config.phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <nav className="main-nav">
        <div className="nav-inner">

          {/* Logo */}
          <div
            className="nav-left nav-logo-wrap"
            onClick={() => goTo("/")}
            role="button"
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && goTo("/")}
          >
            <StoreLogo />
          </div>

          {/* Desktop center menu */}
          <div className="nav-center">
            <ul className="main-menu">
              {categories.map((cat) => (
                <li key={cat.key} className="menu-item has-sub">
                  <button onClick={() => goTo(`/category/${categorySlug(cat.key)}`)}>
                    {cat.label}
                    <i className="fa-solid fa-chevron-down menu-chevron" />
                  </button>
                  <ul className="submenu">
                    {products.filter(p => p.category === cat.key).map(p => (
                      <li key={p.id}>
                        <button className="submenu-item" onClick={() => navigateProduct(p)}>
                          {p.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
              <li className="menu-item">
                <button onClick={() => goTo('/subscription')}>Membership</button>
              </li>
              <li className="menu-item">
                <button onClick={() => goTo('/contact')}>Contact</button>
              </li>
            </ul>
          </div>

          {/* Right: cart + profile + hamburger */}
          <div className="nav-right">
            {user ? (
              <button className="icon-btn profile-btn" onClick={() => goTo("/profile")} title={firstName}>
                {initial}
              </button>
            ) : (
              <button className="icon-btn" onClick={() => goTo("/signin")} title="Login" aria-label="Sign in">
                <i className="fa-regular fa-user" />
              </button>
            )}

            <button className="icon-btn cart-btn" onClick={() => goTo("/cart")} title="Cart" aria-label="Cart">
              <i className="fa-solid fa-cart-shopping" />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </button>

            <button
              className={`hamburger-btn${menuOpen ? ' is-open' : ''}`}
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <span className="ham-line ham-line--top" />
              <span className="ham-line ham-line--mid" />
              <span className="ham-line ham-line--bot" />
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div className="mobile-menu">
            {categories.map((cat) => (
              <div key={cat.key} className="mobile-cat-group">

                <div className="mobile-cat-row">
                  <button
                    className="mobile-cat-label"
                    onClick={() => goTo(`/category/${categorySlug(cat.key)}`)}
                  >
                    {cat.label}
                  </button>
                  <button
                    className={`mobile-cat-toggle${openSub === cat.key ? ' is-open' : ''}`}
                    onClick={() => setOpenSub(openSub === cat.key ? null : cat.key)}
                    aria-label={`Toggle ${cat.label} submenu`}
                  >
                    <i className="fa-solid fa-chevron-down" />
                  </button>
                </div>

                {openSub === cat.key && (
                  <div className="mobile-sub-list">
                    <button
                      className="mobile-sub-view-all"
                      onClick={() => goTo(`/category/${categorySlug(cat.key)}`)}
                    >
                      View All {cat.label}
                      <i className="fa-solid fa-arrow-right mobile-sub-arrow" />
                    </button>
                    {products.filter(p => p.category === cat.key).map(p => (
                      <button
                        key={p.id}
                        className="mobile-sub-item"
                        onClick={() => navigateProduct(p)}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <button className="mobile-nav-link" onClick={() => goTo('/subscription')}>
              <i className="fa-solid fa-gift mobile-nav-icon" />
              Membership
            </button>

            <button className="mobile-nav-link" onClick={() => goTo('/contact')}>
              <i className="fa-regular fa-envelope mobile-nav-icon" />
              Contact
            </button>

            {user ? (
              <button
                className="mobile-nav-link mobile-nav-link--danger"
                onClick={() => { setShowLogout(true); setMenuOpen(false); }}
              >
                <i className="fa-solid fa-arrow-right-from-bracket mobile-nav-icon" />
                Sign Out
              </button>
            ) : (
              <button
                className="mobile-nav-link mobile-nav-link--primary"
                onClick={() => goTo('/signin')}
              >
                <i className="fa-regular fa-user mobile-nav-icon" />
                Sign In
              </button>
            )}
          </div>
        )}
      </nav>

      {showLogout && (
        <LogoutModal user={user} onConfirm={handleLogout} onCancel={() => setShowLogout(false)} />
      )}
    </>
  );
}
